/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
*  Copyright 2019 Adobe
*  All Rights Reserved.
*
* NOTICE:  All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

import PerformanceServiceBase from "bundle/service/PerformanceService";
import ServiceRegistry from "bundle/util/ServiceRegistry";
import { LoggingLevel } from "app/preview/service/LoggingService";
import { getBrowserOSDeviceInfo } from "bundle/util/AppUtil";
import { getEmbedApiVersion } from "app/util/AppUtil";

export default class PerformanceService extends PerformanceServiceBase {
    _shouldSetPreviewLoadedCondition() {
        return !this._appStore.isValidLicense
            || this._appStore.appRenderingBlocked;
    }

    setFileDownloadStartedOn() {
        this._fileDownloadStartedOn = new Date().getTime();
    }

    emitPerformanceObject(logMessage, logObject) {
        ServiceRegistry.getService("LoggingService").emit(
            "PerformanceService",
            LoggingLevel.PERF,
            logMessage,
            logObject,
        );
    }

    getSessionEvent() {
        const browserOSInfo = getBrowserOSDeviceInfo();
        const config = this._appStore.configStore.getConfig();
        const previewConfig = this._appStore.configStore.getActionConfig("preview");
        const event = {
            clientID: config.clientId,
            url: config.windowHref,
            domain: config.eventOrigin,
            sessionID: config.sessionId,
            viewer: (previewConfig.enableModernViewer === false) ? "CLASSIC" : "MODERN",
            embedApiVersion: getEmbedApiVersion(),
            fileInfo: {
                size: this._fileSize,
                noOfPages: this._appStore.pdfMetadata.numPages,
            },
            embedMode: previewConfig.embedMode,
            performance: {
                isLinearizationEnabled: false,
                downloadTime: -1,
                renderTime: -1,
            },
            browserInfo: {
                browser: browserOSInfo.browser,
                browserMajor: browserOSInfo.browserMajor,
            },
            osInfo: {
                os: browserOSInfo.os,
                osVersion: browserOSInfo.osVersion,
            },
            deviceType: browserOSInfo.deviceType,
            generatedAt: new Date().getTime(),
        };

        if (previewConfig.enableLinearization && !this._errorDuringLinearizationFlow) {
            event.performance.isLinearizationEnabled = true;
            event.performance.downloadTime = Math.ceil(
                this._completeFileLoadedOn - this._fileDownloadStartedOn,
            );
        } else if (this._fileDownloadCompletedOn !== -1) {
            event.performance.downloadTime = Math.ceil(
                this._fileDownloadCompletedOn - this._fileDownloadStartedOn,
            );
        }

        if (this._previewLoaded) {
            event.performance.renderTime = Math.ceil(
                this._previewLoaded - this._appStartTime,
            );
        }

        // To represent password protected files cases.
        if (this._previewLoaded === undefined && this._isEventEmitted) {
            event.performance.renderTime = 0;
        }

        return event;
    }
}
